import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import LuvmeIcon from "@/components/LuvmeIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Le password non coincidono."); return; }
    if (password.length < 8 || !/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
      setError("La password deve avere almeno 8 caratteri, con maiuscola, minuscola e un numero.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <LuvmeIcon className="w-7 h-7" />
          <span className="text-2xl font-black font-heading">Luvme</span>
        </Link>

        {done ? (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-7 text-center" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-4" />
            <h1 className="text-xl font-black font-heading mb-2">Password aggiornata</h1>
            <p className="text-white/60 text-sm">Ti stiamo reindirizzando al login…</p>
          </div>
        ) : !token ? (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-7 text-center" data-testid="reset-invalid">
            <p className="text-white/70">Link non valido. Richiedi un nuovo recupero password.</p>
            <Link to="/recupero-password" className="text-[#E91E8C] hover:underline text-sm mt-4 inline-block">Recupera password</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-6 h-6 text-[#E91E8C]" />
              <h1 className="text-3xl font-black font-heading">Nuova password</h1>
            </div>
            <p className="text-white/60 mb-8">Scegli una password sicura per il tuo account.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-white/80">Nuova password</Label>
                <Input
                  type="password" required minLength={8} value={password} data-testid="reset-password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="Min 8 caratteri, maiuscola, minuscola e numero"
                />
              </div>
              <div>
                <Label className="text-white/80">Conferma password</Label>
                <Input
                  type="password" required value={confirm} data-testid="reset-confirm"
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="Ripeti la password"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
              <Button
                type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reimposta password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
